import type { OnInit } from '@angular/core';
import { Component, computed, inject, input } from '@angular/core';
import type { TypedComponentRendering } from '@core/jss-models';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import {
  initOrderCustomerType,
  OrderState,
  addOrderRenderingAction,
  getConsumptionValuesVM,
  getCorrespondenceInstallationAddressHouseNumberExtensionAsDropdownItems,
  getFunnelSettings,
  getOrderAddressProgressiveFormState,
  getOrderConfirmationProgressiveFormState,
  getOrderCrossSellEnabled,
  getOrderCrossSellProduct,
  getOrderCrossSellProgressiveFormState,
  getOrderPaymentProgressiveFormState,
  getOrderPersonalProgressiveFormState,
  getOrderCustomerTypeProgressiveFormState,
  getPropositionOffer,
  getSelectedCorrespondenceInstallationAddressState,
  getSupplyAddressVM,
  initOrderAction,
  initOrderCrossSell,
  openModalFromFunnelSettingsAction,
  getFeatureFlags,
} from '@innogy/become-a-customer/state';
import type { RadioItem } from '@innogy/common-ui/forms';
import {
  SIMPLE_PROGRESSIVE_FORMS_CONFIG,
  SimpleProgressiveFormsConfigService,
} from '@innogy/common-ui/progressive-forms';
import { ProgressiveFormService } from '@innogy/shared/progressive-form';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { GenericModalSources } from '@legacy/common-ui-shared-interfaces';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { showAomModalAction } from '@contracting/modals';

import type { ScOrderFields } from './order-fulfillment-container.model';
import {
  CUSTOMER_TYPE_ORDER_CROSS_SELL_FLOW_STEP_INDEX,
  CUSTOMER_TYPE_ORDER_REGULAR_FLOW_STEP_INDEX,
  ORDER_CROSS_SELL_FLOW_STEP_INDEX,
  ORDER_REGULAR_FLOW_STEP_INDEX,
  ORDER_STEP,
} from './order-fulfillment-steps/constants';
import { scRenderingContentMapper } from './order-fullfillment-container.mapper';
import {
  OrderFulfillmentAddressComponent,
  OrderFulfillmentConfirmationComponent,
  OrderFulfillmentCrossSellComponent,
  OrderFulfillmentPaymentComponent,
  OrderRedirectToMijnComponent,
  OrderFulfillmentCustomerTypeComponent,
  OrderFulfillmentPersonalComponent,
} from './order-fulfillment-steps';

@Component({
  selector: 'wl-order-fulfillment-container',
  templateUrl: './order-fulfillment-container.component.html',
  providers: [
    {
      provide: SIMPLE_PROGRESSIVE_FORMS_CONFIG,
      useValue: {
        scrollIntoViewWhenActive: true,
        enableAutomaticFocusing: true,
      },
    },
    SimpleProgressiveFormsConfigService,
  ],
  standalone: true,
  imports: [
    CommonModule,
    OrderFulfillmentPersonalComponent,
    OrderFulfillmentAddressComponent,
    OrderFulfillmentCrossSellComponent,
    OrderFulfillmentConfirmationComponent,
    OrderFulfillmentPaymentComponent,
    OrderFulfillmentCustomerTypeComponent,
    OrderRedirectToMijnComponent,
    AutomationIdDirective,
  ],
})
export class OrderFulfillmentContainerComponent implements OnInit {
  ORDER_STEP = ORDER_STEP;

  private readonly store$ = inject(Store<OrderState>);
  private readonly progressiveFormConfigService = inject(
    SimpleProgressiveFormsConfigService
  );
  private readonly mediaObserverService = inject(MediaObserverService);
  readonly progressiveFormService = inject(ProgressiveFormService);

  readonly rendering = input.required<TypedComponentRendering<ScOrderFields>>();
  readonly existingCustomerChecked = input(false);

  fulfillmentPersonalProgressiveFormState = this.store$.selectSignal(
    getOrderPersonalProgressiveFormState
  );
  fulfillmentPaymentProgressiveFormState = this.store$.selectSignal(
    getOrderPaymentProgressiveFormState
  );
  fulfillmentAddressProgressiveFormState = this.store$.selectSignal(
    getOrderAddressProgressiveFormState
  );
  fulfillmentCrossSellProgressiveFormState = this.store$.selectSignal(
    getOrderCrossSellProgressiveFormState
  );
  fulfillmentCustomerTypeProgressiveFormState = this.store$.selectSignal(
    getOrderCustomerTypeProgressiveFormState
  );
  fulfillmentConfirmationProgressiveFormState = this.store$.selectSignal(
    getOrderConfirmationProgressiveFormState
  );
  offer = this.store$.selectSignal(getPropositionOffer);
  correspondenceInstallationAddressState = this.store$.selectSignal(
    getSelectedCorrespondenceInstallationAddressState
  );
  correspondenceHouseNumberExtensions = this.store$.selectSignal(
    getCorrespondenceInstallationAddressHouseNumberExtensionAsDropdownItems
  );
  funnelSettings = this.store$.selectSignal(getFunnelSettings);
  supplyAddressVM = this.store$.selectSignal(getSupplyAddressVM);
  crossSellProduct = this.store$.selectSignal(getOrderCrossSellProduct);
  crossSellFeatureEnabled = this.store$.selectSignal(getOrderCrossSellEnabled);
  consumptionValuesVM = this.store$.selectSignal(getConsumptionValuesVM);
  thuisvoordeelAddonEnabled = this.store$.selectSignal(
    getFeatureFlags('crossSellThuisvoordeelAddon')
  );

  readonly customerTypeEnabled = computed(
    () => this.content().enableCustomerTypeStep
  );

  readonly content = computed(() => scRenderingContentMapper(this.rendering()));
  readonly genderOptions = computed(() =>
    this.content().salutationOptions.map(this.mapToString)
  );
  readonly residenceOrWorkplaceOptions = computed(() =>
    this.content().residenceOrWorkplaceOptions.map(this.mapToBoolean)
  );
  readonly exclusivelyCorporateUsageOptions = computed(() =>
    this.content().exclusivelyCorporateUsageOptions.map(this.mapToBoolean)
  );

  ngOnInit(): void {
    this.store$.dispatch(
      addOrderRenderingAction({ rendering: this.rendering() })
    );

    this.store$.dispatch(
      initOrderCrossSell({
        deviceInfo: this.mediaObserverService.deviceInfo(),
        thuisvoordeelAddonEnabled: !!this.thuisvoordeelAddonEnabled(),
      })
    );
    this.store$.dispatch(initOrderCustomerType());

    if (this.crossSellFeatureEnabled()) {
      if (this.content().enableCustomerTypeStep) {
        this.progressiveFormConfigService.totalStepsInFlow.set(6);
      } else {
        this.progressiveFormConfigService.totalStepsInFlow.set(5);
      }
    } else if (this.content().enableCustomerTypeStep) {
      this.progressiveFormConfigService.totalStepsInFlow.set(5);
    } else {
      this.progressiveFormConfigService.totalStepsInFlow.set(4);
    }
  }

  changeSupplyAddress() {
    this.store$.dispatch(
      openModalFromFunnelSettingsAction({
        payload: {
          source: GenericModalSources.CHANGE_SUPPLY_ADDRESS,
        },
      })
    );
  }

  inswitchOpenLegalModal() {
    this.store$.dispatch(
      openModalFromFunnelSettingsAction({
        payload: {
          source: GenericModalSources.INSWITCH_LEGAL_MODAL,
        },
      })
    );
  }

  openAOMModal(offer: Offer) {
    this.store$.dispatch(
      showAomModalAction({
        offer,
        showFooter: false,
        rendering: this.rendering(),
        consumptionValuesVM: this.consumptionValuesVM(),
      })
    );
  }

  continueAsNewCustomer() {
    this.store$.dispatch(initOrderAction());
  }

  stepNumber(step: ORDER_STEP): number {
    if (this.crossSellFeatureEnabled()) {
      if (this.content().enableCustomerTypeStep) {
        return CUSTOMER_TYPE_ORDER_CROSS_SELL_FLOW_STEP_INDEX[step];
      } else {
        return ORDER_CROSS_SELL_FLOW_STEP_INDEX[step];
      }
    } else if (this.content().enableCustomerTypeStep) {
      return CUSTOMER_TYPE_ORDER_REGULAR_FLOW_STEP_INDEX[step];
    } else {
      return ORDER_REGULAR_FLOW_STEP_INDEX[step];
    }
  }

  private readonly mapToString = ({ fields }: any): RadioItem => ({
    label: fields.label.value,
    value: fields.value.value,
    sublabel: fields.sublabel,
    tooltip: fields.tooltip?.value
      ? {
          description: fields.tooltip.value,
        }
      : undefined,
    trackingLabel: fields.trackingLabel?.value,
    disabled: fields.disabled?.value ?? false,
  });

  private readonly mapToBoolean = (option: any): RadioItem => ({
    ...this.mapToString(option),
    value: option.fields.value.value === 'true',
  });
}
